<script>
import AboutView from './AboutView.vue';
import ContactView from './ContactView.vue';
import ProjectsView from './ProjectsView.vue';
import Navigation from '../components/Navigation.vue';
import Skills from '../components/Skills.vue';
import ProjectDetailView from './ProjectDetailView.vue';

export default {
  name: 'Home',
  components: { AboutView, ContactView, ProjectsView, Navigation, Skills, ProjectDetailView },
  data() {
    return {
      roles: ['QA Engineer', 'Frontend Developer', 'Photographer', 'Problem Solver'],
      roleIndex: 0,
      selectedProject: null,
    };
  },
  mounted() {
    this.interval = setInterval(() => {
      this.roleIndex = (this.roleIndex + 1) % this.roles.length;
    }, 2500);
  },
  beforeUnmount() {
    clearInterval(this.interval);
  },
  methods: {
    showProjectDetails(project) { this.selectedProject = project },
    closeProjectDetails() { this.selectedProject = null },
  },
};
</script>

<template>
  <Navigation />
  <div class="home">

    <section id="home" class="hero">
      <div class="blob blob-1" />
      <div class="blob blob-2" />
      <div class="hero-text container">
        <p class="hero-name">Naelia Salas</p>

        <p class="hero-tagline">Building skills, one project at a time</p>

        <hr class="animated-line">

        <p class="hero-role">{{ roles[roleIndex] }}</p>

        <div class="hero-cta">
          <a href="#about"><button class="btn-primary">About me</button></a>
          <a href="#projects"><button class="btn-secondary">View work</button></a>
        </div>
      </div>
    </section>

    <section id="about" class="about-section">
      <AboutView />
    </section>
    <section id="skills">
      <Skills />
    </section>
    <section id="projects" class="projects-section">
      <ProjectsView @showProjectDetails="showProjectDetails" />
    </section>
    <section id="contact">
      <ContactView />
    </section>
    <ProjectDetailView v-if="selectedProject" :project="selectedProject" @close="closeProjectDetails" />
  </div>
</template>

<style lang="scss" scoped>
.hero {
  position: relative;
  height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  background: linear-gradient(-45deg, #0f0c29, #1a1a2e, #16213e, #0f3460);
  background-size: 400% 400%;
  animation: bgShift 15s ease infinite;

  .hero-text {
    position: relative;
    z-index: 1;
    height: 100%;
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    text-align: center;
    width: 100%;
  }
}

@keyframes bgShift {
  0%   { background-position: 0% 50% }
  50%  { background-position: 100% 50% }
  100% { background-position: 0% 50% }
}

/* Blobs */
.blob {
  position: absolute;
  border-radius: 50%;
  filter: blur(80px);
  pointer-events: none;
}
.blob-1 {
  width: 500px; height: 500px;
  background: rgba(102, 126, 234, 0.2);
  top: -100px; left: -100px;
}
.blob-2 {
  width: 400px; height: 400px;
  background: rgba(118, 75, 162, 0.2);
  bottom: -100px; right: -100px;
}

/* Name */
.hero-name {
  font-size: 0.85rem;
  letter-spacing: 4px;
  text-transform: uppercase;
  color: rgba(255, 255, 255, 0.4);
  margin-bottom: 1.5rem;
  opacity: 0;
  animation: fadeInUp 0.6s forwards ease-out 0.2s;
}

/* Tagline */
.hero-tagline {
  font-size: clamp(2.8rem, 7vw, 5.5rem);
  font-weight: 900;
  line-height: 1.1;
  letter-spacing: -2px;
  background: linear-gradient(-45deg, #667eea, #764ba2, #a8c0ff, #c471ed);
  background-size: 400% 400%;
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  animation: gradientShift 8s ease infinite, fadeInUp 0.6s forwards ease-out 0.3s;
  opacity: 0;
  margin-bottom: 1.5rem;
}

@keyframes gradientShift {
  0%   { background-position: 0% 50% }
  50%  { background-position: 100% 50% }
  100% { background-position: 0% 50% }
}

/* Line */
.animated-line {
  width: 0;
  height: 1px;
  background: linear-gradient(135deg, #667eea, #764ba2);
  border: none;
  margin: 0 auto 1.5rem;
  opacity: 0;
  animation: slideInLine 1s forwards ease-out 0.8s;
}

@keyframes slideInLine {
  to { width: 120px; opacity: 0.6 }
}

/* Role */
.hero-role {
  font-size: 0.85rem;
  letter-spacing: 4px;
  text-transform: uppercase;
  color: rgba(255, 255, 255, 0.4);
  margin-bottom: 3rem;
  min-height: 1.2rem;
  opacity: 0;
  animation: fadeInUp 0.6s forwards ease-out 0.5s;
}

/* CTA */
.hero-cta {
  display: flex;
  gap: 1rem;
  justify-content: center;
  opacity: 0;
  animation: fadeInUp 0.6s forwards ease-out 0.7s;
}

.btn-primary {
  padding: 0.9rem 2.2rem;
  border-radius: 50px;
  font-size: 0.95rem;
  font-weight: 600;
  cursor: pointer;
  border: none;
  background: linear-gradient(135deg, #667eea, #764ba2);
  color: white;
  box-shadow: 0 10px 30px rgba(102, 126, 234, 0.3);
  transition: all 0.3s;
  &:hover { transform: translateY(-3px); box-shadow: 0 15px 40px rgba(102, 126, 234, 0.4) }
}

.btn-secondary {
  padding: 0.9rem 2.2rem;
  border-radius: 50px;
  font-size: 0.95rem;
  font-weight: 600;
  cursor: pointer;
  background: transparent;
  color: rgba(255,255,255,0.6);
  border: 1px solid rgba(255, 255, 255, 0.2);
  transition: all 0.3s;
  &:hover { border-color: #667eea; color: #667eea; transform: translateY(-3px) }
}

@keyframes fadeInUp {
  from { opacity: 0; transform: translateY(30px) }
  to   { opacity: 1; transform: translateY(0) }
}

/* Keep original section spacing */
.about-section { margin-bottom: 150px }
.projects-section { margin-bottom: 100px }

@media (max-width: 768px) {
  .hero-tagline { letter-spacing: -1px }
  .hero-cta { flex-direction: column; align-items: center }
  .blob-1 { width: 300px; height: 300px }
  .blob-2 { width: 250px; height: 250px }
}
</style>
